import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  GeoJsonFillLayer,
  GeoJsonLineLayer,
  MapMarker,
  MapView,
  tangerRasterSource,
  useMap,
  type MapViewport,
} from '@wehunt/map'
import { useTranslation } from '../../i18n'
import { Icon } from '../Icon'
import { SurfaceIconButton } from '../SurfaceIconButton'
import { MapPinSprite, pinMarkerCentreLiftPx, useDPRScale } from '../MapPin'
import { TeamMapLayers } from '../TeamMapLayers'
import { groundFeatureCollection, groundWidthExpression } from '../../lib/mapGeoJson'
import { GROUND_BASE_WIDTH, groundShapes } from '../../lib/mapBorders'
import { pinLabelCssPx } from '../../lib/geo'
import { EMPTY_MAP_VIEW, groundFramePoints } from '../../lib/mapSeedView'
import { useBaseMapTileInfo } from '../../lib/useBaseMapTileInfo'
import type { MapAppearance } from '../../lib/mapAppearance'
import type { GeoArea, GeoPt, MapObject, TeamMap } from '../../lib/types'

/**
 * The plan-hunt stand picker + static drive-row mini map, built on the same
 * `@wehunt/map` MapLibre engine every other read/write map surface in this
 * package runs on. Interactive mode is the drive editor's stand picker; the
 * very same component in non-interactive mode is the read-only mini map on
 * each drive row.
 *
 * Every parcel of the ground is drawn through `TeamMapLayers` — the shared
 * ground/sub-area/forbidden-area pipeline every other migrated map surface
 * uses — rather than this component hand-rolling its own path drawing. That
 * matters for MULTI-PARCEL HONESTY: an imported ground is routinely several
 * disjoint parcels, sometimes with an excluded lake as a hole inside one of
 * them, and `groundShapes`/`groundFeatureCollection` (which `TeamMapLayers`
 * calls) are the ONE place in this package that resolves them correctly —
 * merging touching parcels along their shared seam, keeping disjoint parcels
 * apart, and cutting a hole out of whichever outer actually contains it. The
 * historical bug this component's tests still guard against concatenated
 * every parcel's points into a single ring instead: a shape that does not
 * exist, whose outline runs to the end of one parcel and jumps to the start
 * of the next.
 *
 * The drive's selected base (`areaRings`/`areaOuterCount`) is a plan-hunt-only
 * highlight with no counterpart on the team map, so it is drawn separately
 * from `TeamMapLayers`: synthesized into a one-off `GeoArea` and run through
 * the SAME `groundFeatureCollection` resolver, then painted after
 * `TeamMapLayers` so the highlight sits above the reference borders it is
 * drawn against. Routing it through the shared resolver — rather than
 * hand-rolling the union here — is what keeps this highlight held to the same
 * multi-parcel-honesty guarantee: `areaOuterCount` is measured against
 * `areaRings` AS STORED, and `ringsToPolygons` (which `groundShapes` calls
 * internally) slices by that count BEFORE filtering out rings too short to
 * enclose anything. Filtering first would shift the split — a stub ring among
 * the outers would get promoted to an outer, and the union would merge an
 * excluded lake into the highlighted base.
 */
export interface StandsMapProps {
  /** Candidate pins to render. In interactive mode these are the clickable
   *  stand/tower pins the proximity-click toggles into stands. */
  pins: MapObject[]
  /**
   * Extra pins drawn as NON-interactive markers: the drive's selected "Map pins"
   * (`pinIds`), shown purely so the leader can see what the hunt map will carry.
   * Kept out of `pins` because they are a visibility set, not stand candidates —
   * a camera or gathering place is not a stand, so a click must never toggle one
   * into the drive. Excluded from the proximity-click handler for the same
   * reason. Mirrors mobile's preview, which draws the union of the drive's
   * selected pins and its stands.
   */
  displayPins?: MapObject[]
  /** Which pins are highlighted as selected (dashed ring). */
  selectedIds: ReadonlySet<string>
  /**
   * Every parcel of the ground. A list, not one ring: a ground is routinely
   * several parcels, and both callers this replaces drew a wrong shape — one
   * showed the first parcel only, the other concatenated every parcel into a
   * single ring, which is a shape that does not exist.
   */
  boundaryAreas?: readonly GeoArea[]
  /** The drive's base sub-area, as its whole ring list: drawn highlighted, and the
   *  view seeds/fits to it instead of the whole boundary. Omit when the base is the
   *  whole ground. */
  areaRings?: GeoPt[][]
  /** How many leading entries of `areaRings` are outers; the rest are cut out of
   *  the highlight. Omitted means all of them are outers. */
  areaOuterCount?: number
  /** All of the team's sub-areas, drawn as non-interactive reference borders so
   *  the user can see them while picking stands (CLUB-645). The selected base
   *  (areaRings) is drawn on top of these. */
  subAreaPolygons?: readonly GeoArea[]
  /** Forbidden areas. A hunter picking a stand must see the no-hunting zones. */
  forbiddenPolygons?: readonly GeoArea[]
  /** Required with no default, so a surface cannot silently render at 1.0. */
  appearance: MapAppearance
  teamBorderColor?: string
  /** Interactive = pan/zoom/click-to-toggle + zoom buttons + labels. */
  interactive?: boolean
  onTogglePin?: (id: string) => void
  /** Default: follows `interactive`. */
  showLabels?: boolean
  /** Non-interactive: fit the view to the selected pins (else all `pins`). */
  fitToSelected?: boolean
  background?: string
  style?: React.CSSProperties
}

// Fixed, like ReportMap's own REPORT_MIN/MAX_ZOOM (see that component's
// comment for the full reasoning): `MapView` reads `minZoom`/`maxZoom` once,
// at construction, well before `useBaseMapTileInfo`'s async `/api/maps` fetch
// can possibly have answered, so deriving these from the fetched tile info
// can never take effect. 1/19 matches this map's historical bounds; the
// catalogue's real fetch bounds still live on the raster SOURCE, via
// `tangerRasterSource`'s `minzoom`/`maxzoom`.
const STANDS_MIN_ZOOM = 1
const STANDS_MAX_ZOOM = 19

/** A click within this many CSS px of a pin toggles it — the same forgiving
 *  hit area the hand-rolled SVG engine gave a stand pin. */
const PROXIMITY_PX = 30

// Same crop as `PinMarkerContent`'s GLYPH_W/H: `MapPinSprite`'s legacy 56×47
// coordinate box (tip anchored at (28,47)) only draws into its bottom ~21
// units — see that component's comment for the full derivation. Kept as a
// separate constant here (rather than imported) because `PinMarkerContent`
// is deliberately not reused: this map's pin-colored label pills have no
// counterpart in `PinMarkerContent`'s appearance-driven label styling.
const GLYPH_W = 20
const GLYPH_H = 21

interface GeoBounds {
  minLat: number
  maxLat: number
  minLon: number
  maxLon: number
}

/** The extent of a point set, or `null` for none at all. */
function boundsOf(points: readonly GeoPt[]): GeoBounds | null {
  if (points.length === 0) return null
  let minLat = Infinity
  let maxLat = -Infinity
  let minLon = Infinity
  let maxLon = -Infinity
  for (const p of points) {
    minLat = Math.min(minLat, p.lat)
    maxLat = Math.max(maxLat, p.lat)
    minLon = Math.min(minLon, p.lon)
    maxLon = Math.max(maxLon, p.lon)
  }
  return { minLat, maxLat, minLon, maxLon }
}

/** True for a bounds box with no span in either dimension — one point, or
 *  several at the same place: it fixes where to look but not how far out. */
function hasNoExtent(b: GeoBounds): boolean {
  return b.minLat === b.maxLat && b.minLon === b.maxLon
}

/** Click anywhere within `PROXIMITY_PX` of a pin toggles the nearest one —
 *  rendered inside `MapView`'s children so `useMap()` resolves to the live
 *  map instance. MapLibre only fires `click` when a gesture did NOT pan (it
 *  suppresses the event after an actual drag), so unlike the hand-rolled SVG
 *  engine this needs no separate `hasPanned` guard.
 *
 *  A direct click on a pin's own sprite would otherwise ALSO reach this
 *  handler and toggle the same (nearest) pin a second time: MapLibre's
 *  `Marker.addTo()` appends a marker's DOM element into `map.getCanvasContainer()`
 *  — the very same container element MapLibre's own click detection listens
 *  on — so a marker click bubbles up to that listener exactly like any other
 *  click on the map; nothing in MapLibre itself excludes a marker as a click
 *  source. What actually prevents the double count is `MapPinSprite` calling
 *  `stopPropagation()` on its own click (only when `interactive`, see
 *  `MapPin.tsx`) before the event can bubble that far — not a `hasPanned`-style
 *  guard here, and not marker/canvas DOM siblinghood (they're both children of
 *  the same container, so siblinghood alone would not stop the bubble). */
function ProximityClickHandler({
  pins,
  onTogglePin,
}: {
  pins: MapObject[]
  onTogglePin?: (id: string) => void
}) {
  const map = useMap()
  useEffect(() => {
    if (!map || !onTogglePin) return
    const handleClick = (e: { point: { x: number; y: number } }) => {
      let nearestId: string | null = null
      let nearestDist = PROXIMITY_PX
      for (const pin of pins) {
        const p = map.project([pin.lon, pin.lat])
        const dist = Math.hypot(p.x - e.point.x, p.y - e.point.y)
        if (dist < nearestDist) {
          nearestId = pin.id
          nearestDist = dist
        }
      }
      if (nearestId) onTogglePin(nearestId)
    }
    map.on('click', handleClick)
    return () => {
      map.off('click', handleClick)
    }
  }, [map, pins, onTogglePin])
  return null
}

/** The zoom stack, rendered inside `MapView`'s children so `useMap()`
 *  resolves to the live map instance. Disabled at this map's fixed 1/19
 *  bounds — the same bounds `MapView` itself is constructed with. */
function StandsZoomControls({
  zoom,
  zoomInTitle,
  zoomOutTitle,
}: {
  zoom: number
  zoomInTitle: string
  zoomOutTitle: string
}) {
  const map = useMap()
  return (
    <div
      style={{ position: 'absolute', top: 16, right: 16, display: 'flex', flexDirection: 'column', gap: 6, zIndex: 4 }}
    >
      <SurfaceIconButton title={zoomInTitle} onClick={() => map?.zoomIn()} disabled={zoom >= STANDS_MAX_ZOOM}>
        <Icon name="zoom-in" size={20} />
      </SurfaceIconButton>
      <SurfaceIconButton title={zoomOutTitle} onClick={() => map?.zoomOut()} disabled={zoom <= STANDS_MIN_ZOOM}>
        <Icon name="zoom-out" size={20} />
      </SurfaceIconButton>
    </div>
  )
}

/** The DOM content of one stand-pin marker — deliberately NOT a
 *  `PinMarkerContent` variant: this map's label pills are coloured by the
 *  PIN's own colour (a plan-hunt selection affordance), not by the shared
 *  appearance-driven label styling every other pin surface uses. Anchor the
 *  enclosing `MapMarker` 'bottom' — the sprite's tip is this element's
 *  bottom-center, the same crop trick `PinMarkerContent` uses. */
function StandPinContent({
  pin,
  selected,
  scale,
  labelFontSize,
  showLabel,
  interactive,
  onToggle,
}: {
  pin: MapObject
  selected: boolean
  scale: number
  labelFontSize: number
  showLabel: boolean
  interactive: boolean
  onToggle?: (id: string) => void
}) {
  // See `PinMarkerContent`'s comment for the full reasoning: MapPinSprite
  // multiplies its `scale` prop by `useDPRScale()` internally to compensate
  // the OLD SVG engine's window-dependent viewBox stretch. A DOM marker has
  // nothing left to cancel, so this divides the same factor back out to keep
  // the net on-screen size at exactly `scale`.
  const dprScale = useDPRScale()
  const spriteScale = dprScale > 0 ? scale / dprScale : scale
  const labelColor = pin.color || '#ffffff'
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {showLabel && pin.label && (
        <span
          style={{
            fontFamily: 'Geist, ui-sans-serif, system-ui, sans-serif',
            fontSize: labelFontSize,
            lineHeight: 1.2,
            fontWeight: 600,
            color: labelColor,
            background: 'rgba(0,0,0,0.5)',
            border: `1px solid ${labelColor}`,
            borderRadius: 4,
            padding: '1px 5px',
            whiteSpace: 'nowrap',
            marginBottom: 2,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          {pin.label}
        </span>
      )}
      <svg
        width={GLYPH_W * scale}
        height={GLYPH_H * scale}
        viewBox={`${28 - (GLYPH_W / 2) * scale} ${47 - GLYPH_H * scale} ${GLYPH_W * scale} ${GLYPH_H * scale}`}
        // This map always draws backgrounded pins (MapPinSprite's default).
        style={{ display: 'block', overflow: 'visible', marginBottom: -pinMarkerCentreLiftPx(pin.type, true, scale) }}
      >
        <MapPinSprite
          kind={pin.type}
          symbol={pin.customSymbol}
          x={28}
          y={47}
          selected={selected}
          color={pin.color}
          cursor={interactive ? 'pointer' : 'default'}
          scale={spriteScale}
          interactive={interactive}
          onClick={interactive ? () => onToggle?.(pin.id) : undefined}
        />
      </svg>
    </div>
  )
}

export function StandsMap({
  pins,
  displayPins = [],
  selectedIds,
  boundaryAreas = [],
  areaRings = [],
  areaOuterCount,
  subAreaPolygons = [],
  forbiddenPolygons = [],
  appearance,
  teamBorderColor,
  interactive = true,
  onTogglePin,
  showLabels,
  fitToSelected = false,
  background = '#1a1e1c',
  style,
}: StandsMapProps) {
  const { t } = useTranslation('planHuntDrives')
  const labels = showLabels ?? interactive
  const tileInfo = useBaseMapTileInfo()

  const baseSource = useMemo(
    () => (tileInfo ? tangerRasterSource({ ...tileInfo, widths: [] }) : null),
    [tileInfo],
  )

  // The drive's selected base, as a one-off GeoArea so it can go through the
  // very same union resolver every other ground layer uses (see file doc
  // comment above on why that matters for multi-parcel honesty).
  const driveBaseArea = useMemo<GeoArea>(
    () => ({ id: 'drive-base', name: '', rings: [...areaRings], outerCount: areaOuterCount ?? areaRings.length }),
    [areaRings, areaOuterCount],
  )
  const driveBaseShapes = useMemo(() => groundShapes([driveBaseArea], 'subarea'), [driveBaseArea])
  const driveBaseFC = useMemo(() => groundFeatureCollection([driveBaseArea], 'subarea'), [driveBaseArea])
  // The RESOLVED (unioned) points, for framing only. Flattening every parcel is
  // legitimate HERE and only here: framing reads min/max over a bag of points,
  // while DRAWING a ring from concatenated points is the historical bug this
  // component's tests guard against — drawing goes through `driveBaseFC` above,
  // which keeps every parcel's rings apart.
  const areaPoints = useMemo(() => driveBaseShapes[0]?.rings.flat() ?? [], [driveBaseShapes])
  // Border ring points, or a storage-offloaded ground's [SW, NE] bbox when its
  // polygon never came down — shared with the team map so a bounds-only ground
  // frames here too instead of dropping to the regional overview.
  const boundaryPoints = useMemo(() => groundFramePoints(boundaryAreas), [boundaryAreas])
  // A boundary needs three points before it bounds anything — two points are a
  // line the drawing flow never completes.
  const framePoints = areaPoints.length >= 3 ? areaPoints : boundaryPoints

  const teamMap = useMemo<TeamMap>(
    () => ({
      name: '',
      updated: '',
      boundaryAreas: [...boundaryAreas],
      subAreaPolygons: [...subAreaPolygons],
      forbiddenPolygons: [...forbiddenPolygons],
      borderColor: teamBorderColor,
      objects: [],
      lines: [],
    }),
    [boundaryAreas, subAreaPolygons, forbiddenPolygons, teamBorderColor],
  )

  // Re-centre (snap back) only when the drive's base area changes — the same
  // trigger the hand-rolled SVG engine reset on. `MapView` reads its
  // `initialBounds`/`initialView` once, at mount, so remounting on THIS key is
  // how the "jump back" happens now. Keyed by the polygon's COORDINATES, not
  // its reference: the default `[]` is a fresh array every render and must
  // not retrigger a remount.
  const areaKey = areaPoints.map((p) => `${p.lat},${p.lon}`).join(';')

  const framingPins = fitToSelected ? pins.filter((p) => selectedIds.has(p.id)) : pins
  // A non-interactive map has no pan/zoom state worth protecting, so — unlike
  // the interactive picker above, which recentres only on a base-area change —
  // it also remounts (and re-fits) whenever the pins it frames change, keeping
  // a drive row's thumbnail in sync as stands are picked or removed.
  const pinsKey = framingPins.map((p) => `${p.id}:${p.lat},${p.lon}`).join(';')
  const mapKey = interactive ? areaKey : `${areaKey}|${pinsKey}`

  const content = useMemo(() => {
    if (interactive)
      return [
        ...framePoints,
        ...pins.map((p) => ({ lat: p.lat, lon: p.lon })),
        ...displayPins.map((p) => ({ lat: p.lat, lon: p.lon })),
      ]
    const target = framingPins.length ? framingPins : framePoints
    return target.map((p) => ({ lat: p.lat, lon: p.lon }))
  }, [interactive, framePoints, pins, displayPins, framingPins])

  const bounds = useMemo(() => boundsOf(content), [content])
  const initialBounds = useMemo<[number, number, number, number] | null>(() => {
    if (!bounds || hasNoExtent(bounds)) return null
    return [bounds.minLon, bounds.minLat, bounds.maxLon, bounds.maxLat]
  }, [bounds])
  // A lone stand (or several stacked at the same spot) fixes where to look but
  // not how far out; 14 keeps the surrounding ground readable instead of
  // cropping to the deepest zoom the tiles have.
  const initialView = useMemo<MapViewport | undefined>(() => {
    if (!bounds) return EMPTY_MAP_VIEW
    if (hasNoExtent(bounds)) return { center: { lat: bounds.minLat, lon: bounds.minLon }, zoom: 14 }
    return undefined
  }, [bounds])

  const [liveZoom, setLiveZoom] = useState(initialView?.zoom ?? STANDS_MIN_ZOOM)
  const handleViewChange = useCallback((v: MapViewport) => setLiveZoom(v.zoom), [])

  // Pin scale: grows with the live zoom but floored so interactive pins never
  // drop below a comfortably clickable size (>=44px effective target on
  // desktop, WCAG 2.5.5 — the sprite hit circle plus the proximity click
  // above). Non-interactive pins are fixed — there is no click target to
  // protect there. Updates on `moveend` (the accepted snap behavior).
  const pinScale = interactive ? Math.max(0.8, Math.min(1.5, liveZoom / 12)) : 0.6
  const labelFontSize = pinLabelCssPx(liveZoom, STANDS_MAX_ZOOM)

  return (
    <div style={{ position: 'relative', background, minHeight: 0, overflow: 'hidden', ...style }}>
      <MapView
        key={mapKey}
        baseSource={baseSource}
        initialBounds={initialBounds}
        initialView={initialView}
        interactive={interactive}
        minZoom={STANDS_MIN_ZOOM}
        maxZoom={STANDS_MAX_ZOOM}
        onViewChange={handleViewChange}
      >
        <TeamMapLayers map={teamMap} appearance={appearance} zoom={liveZoom} subAreaLabels={false} />

        {/* The drive's selected base — a plan-hunt selection affordance with no
            counterpart on the team map, so its colours stay bespoke; only the
            width joins the shared law so it stays proportional to the borders
            around it. Drawn AFTER TeamMapLayers so it sits above the reference
            borders it is highlighted against. */}
        <GeoJsonFillLayer id="drive-base-fill" data={driveBaseFC} paint={{ 'fill-color': 'rgba(62,104,255,0.14)' }} />
        <GeoJsonLineLayer
          id="drive-base-line"
          data={driveBaseFC}
          paint={{
            'line-color': '#8fb0ff',
            'line-width': groundWidthExpression(GROUND_BASE_WIDTH.subarea, appearance.lineThickness.subarea),
          }}
        />

        {pins.map((pin) => (
          <MapMarker key={pin.id} lat={pin.lat} lon={pin.lon} anchor="bottom">
            <StandPinContent
              pin={pin}
              selected={selectedIds.has(pin.id)}
              scale={pinScale}
              labelFontSize={labelFontSize}
              showLabel={labels}
              interactive={interactive}
              onToggle={onTogglePin}
            />
          </MapMarker>
        ))}

        {/* The drive's selected map pins, drawn as a read-only overlay. Never
            interactive, so a click near one does not add it as a stand. A pin
            that is BOTH a stand candidate (in `pins`) and selected is already
            drawn above, so skip it here to avoid a double marker. */}
        {displayPins
          .filter((pin) => !pins.some((p) => p.id === pin.id))
          .map((pin) => (
            <MapMarker key={`display-${pin.id}`} lat={pin.lat} lon={pin.lon} anchor="bottom">
              <StandPinContent
                pin={pin}
                selected={false}
                scale={pinScale}
                labelFontSize={labelFontSize}
                showLabel={labels}
                interactive={false}
              />
            </MapMarker>
          ))}

        {interactive && <ProximityClickHandler pins={pins} onTogglePin={onTogglePin} />}

        {interactive && (
          <StandsZoomControls zoom={liveZoom} zoomInTitle={t('map.zoomIn')} zoomOutTitle={t('map.zoomOut')} />
        )}
      </MapView>
    </div>
  )
}
